import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'

export interface ChatMessage {
  id: string
  order_id: string
  created_at: string
  author_name: string
  body: string
  attachments: { url: string; type: string }[]
}

/**
 * Чат дилера ↔ инспектора по заказу. Раньше был только mock-stub,
 * поэтому в реальном режиме `OrderDetailPage` всегда показывал «нет данных».
 * Теперь читаем из `order_chat_messages` и подтягиваем имя автора отдельным
 * запросом (sender_id ссылается на auth.users — embed через PostgREST
 * невозможен). Доступ ограничен RLS: участники заказа или админ.
 */
export async function listChatMessages(orderId: string): Promise<ChatMessage[]> {
  if (USE_MOCK) {
    return mockState.chatMessages.filter((m) => m.order_id === orderId)
  }

  const supabase = createClient()
  const { data, error } = await supabase
    .from('order_chat_messages')
    .select(
      'id, order_id, sender_id, message_text, attachment_url, attachment_type, created_at',
    )
    .eq('order_id', orderId)
    .order('created_at', { ascending: true })

  if (error) {
    // RLS может запретить доступ (например, не участник заказа) — отдаём
    // пустой чат, чтобы не ломать карточку, но всегда логируем причину:
    // молчаливый return [] однажды уже маскировал баг политики, когда
    // admin/dispatcher не подпадали под select.
    console.warn('[listChatMessages] supabase error', {
      orderId,
      message: error.message,
      code: error.code,
    })
    return []
  }

  const rows = (data ?? []) as Array<{
    id: string
    order_id: string
    sender_id: string | null
    message_text: string | null
    attachment_url: string | null
    attachment_type: string | null
    created_at: string
  }>

  const senderIds = Array.from(
    new Set(rows.map((r) => r.sender_id).filter((v): v is string => !!v)),
  )
  let nameById = new Map<string, string>()
  if (senderIds.length > 0) {
    const { data: profs } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', senderIds)
    nameById = new Map(
      (profs ?? []).map((p) => [
        p.id as string,
        (p.full_name as string | null) ?? '',
      ]),
    )
  }

  return rows.map((r) => ({
    id: r.id,
    order_id: r.order_id,
    created_at: r.created_at,
    author_name: (r.sender_id && nameById.get(r.sender_id)) || '—',
    body: r.message_text ?? '',
    attachments: r.attachment_url
      ? [{ url: r.attachment_url, type: r.attachment_type ?? 'file' }]
      : [],
  }))
}
